import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { taskHistoryResponse } from '../lib/types'
import { formatDateTime, formatDateOnly } from '../lib/format'

/**
 * Task Activity / audit log — the who-changed-what timeline for a single task.
 * Reads `GET /lists/:listId/tasks/:taskId/history` (append-only TaskHistory) and
 * renders each field change (status, priority, due/start date, name,
 * description, assignee add-remove) plus comment / attachment / watcher events,
 * newest first, with the actor's name and the exact time.
 *
 * History is recorded from the point the feature shipped — edits made before
 * then weren't captured, so an older task simply shows fewer (or no) rows.
 */

const PRIORITY_LABEL = {
  urgent: 'Urgent',
  high: 'High',
  normal: 'Normal',
  low: 'Low',
}

/** A stored value rendered for display; `null`/empty shows a muted "None". */
function val(v) {
  if (v == null || v === '') {
    return <span className="italic text-[color:var(--color-ink-subtle)]">None</span>
  }
  return <span className="font-medium text-[color:var(--color-ink)]">{v}</span>
}

function dateVal(iso, hasTime) {
  if (!iso) {
    return <span className="italic text-[color:var(--color-ink-subtle)]">None</span>
  }
  return (
    <span className="font-medium text-[color:var(--color-ink)]">
      {hasTime ? formatDateTime(iso) : formatDateOnly(iso)}
    </span>
  )
}

/** Old → new value pair with an arrow. */
function fromTo(from, to) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {from} <span className="text-[color:var(--color-ink-subtle)]">→</span> {to}
    </span>
  )
}

/** Build the human-readable description for one history row. Returns a
 *  React node: a leading verb phrase plus, where relevant, the old→new pair. */
function describe(e) {
  const p = e.payload && typeof e.payload === 'object' ? e.payload : {}
  switch (e.eventType) {
    case 'status_changed':
      return (
        <>
          changed <b>Status</b> {fromTo(val(p.from), val(p.to))}
        </>
      )
    case 'priority_changed':
      return (
        <>
          changed <b>Priority</b>{' '}
          {fromTo(
            val(p.from ? (PRIORITY_LABEL[p.from] ?? p.from) : null),
            val(p.to ? (PRIORITY_LABEL[p.to] ?? p.to) : null),
          )}
        </>
      )
    case 'due_date_changed':
      return (
        <>
          changed <b>Due date</b>{' '}
          {fromTo(dateVal(p.from, p.fromHasTime), dateVal(p.to, p.toHasTime))}
        </>
      )
    case 'start_date_changed':
      return (
        <>
          changed <b>Start date</b>{' '}
          {fromTo(dateVal(p.from, p.fromHasTime), dateVal(p.to, p.toHasTime))}
        </>
      )
    case 'name_changed':
      return (
        <>
          renamed the task {fromTo(val(p.from), val(p.to))}
        </>
      )
    case 'description_changed':
      return (
        <>
          updated the <b>Description</b>
        </>
      )
    case 'assignee_added':
      return (
        <>
          assigned {val(p.userName ?? 'a member')}
        </>
      )
    case 'assignee_removed':
      return (
        <>
          unassigned {val(p.userName ?? 'a member')}
        </>
      )
    case 'comment_added':
      return 'added a comment'
    case 'comment_added_internal':
      return 'added an internal note'
    case 'comment_edited':
      return 'edited a comment'
    case 'comment_removed':
      return 'removed a comment'
    case 'comment_removed_internal':
      return 'removed an internal note'
    case 'attachment_added':
      return 'added a file or link'
    case 'attachment_removed':
      return 'removed a file or link'
    case 'watcher_added':
      return 'started watching this task'
    case 'watcher_removed':
      return 'stopped watching this task'
    case 'checker_changed':
      return (
        <>
          set the <b>checker</b> to {val(p.toName ?? 'a member')}
        </>
      )
    case 'checker_removed':
      return (
        <>
          removed the <b>checker</b> {val(p.fromName ?? 'a member')}
        </>
      )
    case 'verification_submitted':
      return 'submitted this task for verification'
    case 'verification_approved':
      return (
        <>
          approved <b>verification</b>
        </>
      )
    case 'verification_rejected':
      return (
        <>
          rejected <b>verification</b> — requested changes
        </>
      )
    default:
      // Legacy / unknown kinds (e.g. retired timer events) render readably.
      return e.eventType.replace(/_/g, ' ')
  }
}

export function TaskActivity({ taskId, listId }) {
  const query = useQuery({
    queryKey: ['task-history', taskId],
    queryFn: async () => {
      const res = await api.get(`/lists/${listId}/tasks/${taskId}/history`)
      return taskHistoryResponse.parse(res.data)
    },
    enabled: Boolean(taskId && listId),
  })

  // Newest first — the timeline reads top-down from the latest change.
  const rows = [...(query.data?.data ?? [])].reverse()

  return (
    <div className="px-5 py-4 border-t border-[color:var(--color-rule)]">
      <h3 className="text-sm uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] mb-3 mt-0">
        Activity
      </h3>

      {query.isLoading ? (
        <p className="text-sm text-[color:var(--color-ink-subtle)]">
          Loading activity…
        </p>
      ) : query.isError ? (
        <p className="text-sm text-[color:var(--color-ink-subtle)]">
          Activity could not be loaded.
        </p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-[color:var(--color-ink-subtle)]">
          No activity recorded yet.
        </p>
      ) : (
        <ul className="flex flex-col gap-3 max-h-72 overflow-y-auto pr-1">
          {rows.map((e) => (
            <li key={e.id} className="flex gap-3 text-sm">
              <span
                aria-hidden="true"
                className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[color:var(--color-accent)]"
              />
              <div className="min-w-0">
                <p className="text-[color:var(--color-ink)] m-0 leading-snug">
                  <b>{e.actorFullName ?? 'Someone'}</b> {describe(e)}
                </p>
                <p
                  className="text-xs text-[color:var(--color-ink-subtle)] m-0 mt-0.5"
                  title={formatDateTime(e.createdAt)}
                >
                  {formatDateTime(e.createdAt)}
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
